import * as Tone from 'tone';
import { Chord, ChordProgression } from '../types';
import Malakai, { MalakaiConfig } from '../Malakai';
import { GenerationMode } from '../generators/BaseGenerator';
import * as INSTRUMENTS from '../catalog/instruments';
import LEADS from '../catalog/leads';
import { MusicRNNGenerator, MusicVAEGenerator } from '../generators';

let startProgression!: ChordProgression;
let middleProgression!: ChordProgression;
let endProgression!: ChordProgression;

function getCurrentProgression(progress: number): ChordProgression {
  if (progress < 0.33) {
    return startProgression;
  }
  if (progress < 0.66) {
    return middleProgression;
  }
  return endProgression;
}

export default <MalakaiConfig>{
  name: 'Atticus with Hangdrum',
  description: 'Just an experiment. Does not represent my music preferences.',
  tempo: 120,
  duration: '24m',
  quantizeCurve: true,
  sliders: [
    {
      key: 'temperature',
      name: 'Generator Temperature',
      description: 'Randomness of generator.',
      value: 1,
      min: 0,
      max: 2,
    },
    {
      key: 'similarity',
      name: 'Generator Similarity',
      description: 'Similarity of generated melody.',
      value: 0.75,
    },
  ],
  generators: [
    {
      name: 'bassline',
      lead: LEADS.SomeKindOfMagic,
      generator: new MusicVAEGenerator({
        // https://github.com/magenta/magenta-js/blob/master/music/checkpoints/README.md
        checkpoint: 'https://storage.googleapis.com/magentadata/js/checkpoints/music_vae/mel_2bar_small',
        mode: GenerationMode.Looping,
        tempo: 60,
        sequenceDuration: '4m',
        lookbackDuration: '4m',
        conditionGenerator: (malakai: Malakai, progress) => ({
          samples: 8,
          similarity: malakai.getSliderValue('similarity'),
          temperature: malakai.getSliderValue('temperature'),
          chordProgression: getCurrentProgression(progress),
          key: 0,
        }),
      }),
    },
    {
      name: 'melody',
      lead: LEADS.SomeKindOfMagic,
      generator: new MusicRNNGenerator({
        // https://github.com/magenta/magenta-js/blob/master/music/checkpoints/README.md
        checkpoint: 'https://storage.googleapis.com/magentadata/js/checkpoints/music_rnn/chord_pitches_improv',
        mode: GenerationMode.Looping,
        tempo: 60,
        sequenceDuration: '4m',
        lookbackDuration: '4m',
        conditionGenerator: (malakai: Malakai, progress) => ({
          temperature: malakai.getSliderValue('temperature'),
          chordProgression: getCurrentProgression(progress),
        }),
      }),
    },
  ],
  layers: [
    // {
    //   name: 'metronome',
    //   instruments: {
    //     met: INSTRUMENTS.Metronome,
    //   },
    //   loops: [
    //     {
    //       start: 0,
    //       interval: '4n',
    //       callback({ layer }, time) {
    //         layer.instruments.met.triggerAttackRelease('C5', '4n', time, 0.5);
    //       },
    //     },
    //   ],
    // },
    {
      name: 'controller',
      events: [
        {
          start: '4m',
          callback({ malakai }) {
            malakai.setLayerActive('melody', true);
          },
        },
        {
          start: '20m',
          callback({ malakai }) {
            malakai.setLayerActive('bassline', false);
            malakai.setLayerActive('melody', false);
          },
        },
      ],
    },
    {
      name: 'pad',
      instruments: {
        bass: INSTRUMENTS.LeadShakahuchiFlute,
      },
      loops: [
        {
          start: 0,
          interval: '8m',
          callback({ transport, layer }, time) {
            const progress = transport.getProgressForTime(time);
            const progression = getCurrentProgression(progress);
            const part = new Tone.Part((t, chord: Chord) => {
              const chordDuration = Tone.Time(chord.duration).toSeconds();
              layer.instruments.bass.triggerAttackRelease(
                chord.notes[0],
                chordDuration * 1.1, // overlapping flute
                transport.secondsToTransportTime(t),
                chord.velocity * 0.33,
              );
            }, progression.chords);
            part.loop = 2;
            part.loopStart = 0;
            part.loopEnd = '4m';
            part.start(time - transport.lookAhead);
          },
        },
      ],
    },
    {
      name: 'bassline',
      generator: 'bassline',
      instruments: {
        main: INSTRUMENTS.KeysHangDrumEcho,
      },
      hooks: {
        note({ layer }, event) {
          const { time, frequency, duration } = event;
          if (time && frequency && duration) {
            layer.instruments.main.triggerAttackRelease(frequency, duration, time, 0.05);
          }
        },
      },
    },
    {
      name: 'melody',
      disabled: true,
      generator: 'melody',
      instruments: {
        main: INSTRUMENTS.WarmSmokyPiano,
      },
      hooks: {
        note({ layer }, event) {
          const { time, frequency, duration } = event;
          if (time && frequency && duration) {
            layer.instruments.main.triggerAttackRelease(frequency, duration, time, 0.2);
          }
        },
      },
    },
  ],
  hooks: {
    preStart({ malakai }) {
      startProgression = malakai.findChordProgression(malakai.curve.getValue(0));
      middleProgression = malakai.findChordProgression(malakai.curve.getValue(0.33));
      endProgression = malakai.findChordProgression(malakai.curve.getValue(0.66));
    },
  },
};
